import React, { useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Dialog,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  DialogTitle,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
} from "@material-ui/core";
import { Formik } from "formik";

import * as schoolDUCK from "../../../redux/ducks/school.duck";
import {
  saveSchool,
  updateSchool,
  ORG_PLAN_OPTIONS,
  ORG_TYPE_OPTIONS,
  ORG_STATUS_OPTIONS,
} from "../../crud/api/schools.crud";
import { setLoader } from "../../../redux/ducks/load.duck";
import { GAutocomplete } from "../../utils/GAutocomplete";
import TimeZoneSelector from "./TimeZoneSelector";

const SchoolDialog = ({
  mode,
  setDialog,
  school,
  schoolActions,
  settingLoader,
}) => {
  const title = mode === "editing" ? "Edit Organization" : "New Organization";
  const open = mode === "editing" || mode === "create";
  const [loading, setLoading] = useState(false);

  // TZ-783: dropped from the payload — conference_id (the misnamed
  // "Organization Type" conferences list), mascot_name + mascot_image
  // (retired entirely), is_pro_athlete (now derived from org_type ==
  // "pro" by the backend before_save callback). tier_id and
  // proof_of_concept are kept because they're hidden fields populated
  // from existing school state, not exposed in this form.
  const buildPayload = (values) => ({
    tier_id: values.tier_id,
    name: values.name,
    address: values.address,
    primary_contact: values.primary_contact,
    contact_email: values.contact_email,
    contact_number: values.contact_number,
    proof_of_concept: values.proof_of_concept,
    website_url: values.website_url,
    time_zone: values.time_zone,
    not_great_dialog_copy: values.not_great_dialog_copy,
    show_chatbot: values.show_chatbot,
    active_mindflow: values.active_mindflow,
    plan: values.plan,
    org_type: values.org_type,
    status: values.status,
  });

  const create = async (values) => {
    try {
      await saveSchool(schoolActions.updateSchools, buildPayload(values));
      setDialog("close");
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const update = async (values) => {
    try {
      await updateSchool(schoolActions.updateSchools, buildPayload(values), school.id);
      setDialog("close");
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setDialog("close")}
      maxWidth="md"
      fullWidth
      id="school_dialog"
    >
      <Formik
        initialValues={{
          ...school,
          // Defensive defaults so every controlled input is controlled
          // from mount and buildPayload never serialises `undefined`
          // (FormData.append("status", undefined) would post the literal
          // string "undefined", failing School#validates :status inclusion
          // post-#735). SchoolSerializer currently omits plan/org_type/
          // status (same gap as graduation_year was in StudentSerializer
          // pre-#733) — fixing the serializer is a separate api PR;
          // these fallbacks keep edits safe in the meantime.
          name: school.name || "",
          website_url: school.website_url || "",
          time_zone: school.time_zone || "",
          plan: school.plan || "",
          org_type: school.org_type || "",
          status: school.status || "active",
          // Boolean toggles need an explicit `false` default for
          // the same FormData-multipart reason. Without this,
          // an unticked toggle on the New Organization form keeps
          // values.show_chatbot / .active_mindflow undefined,
          // FormData.append serialises that as the literal string
          // "undefined", and Rails' ActiveModel::Type::Boolean
          // casts any non-{"false","0","f","off"} string to TRUE
          // — so the row lands with the toggle enabled, which
          // (a) was always silently the case for active_mindflow
          // and (b) just started biting show_chatbot now that api
          // PR #743 added it to the CreateSchool permit list.
          show_chatbot: school.show_chatbot ?? false,
          active_mindflow: school.active_mindflow ?? false,
          tier_id: school.tier && school.tier.id ? school.tier.id : "",
        }}
        validate={(values) => {
          const errors = {};
          if (!values.name) errors.name = "Required field";
          if (!values.time_zone || values.time_zone === null)
            errors.time_zone = "Required field";

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setLoading(true);
          setTimeout(() => {
            school.id ? update(values) : create(values);
            setSubmitting(false);
          }, 1000);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form noValidate={true} autoComplete="off" onSubmit={handleSubmit}>
            <DialogTitle disableTypography>
              <Typography variant="h6">{title}</Typography>
            </DialogTitle>
            <DialogContent>
              {/* Section 1: Org details — identifying / contact info.
                  Required: Name + Time Zone (Yup validator enforces). */}
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Org details
              </Typography>
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    id="school_name_input_dialog"
                    label="Organization Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.name && errors.name}
                    error={Boolean(touched.name && errors.name)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TimeZoneSelector
                    time_zone={values.time_zone}
                    setFieldValue={setFieldValue}
                    helperText={touched.time_zone && errors.time_zone}
                    error={Boolean(touched.time_zone && errors.time_zone)}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GAutocomplete
                    initialValue={values.address}
                    setterFunction={setFieldValue}
                    error={touched.address && errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="school_web_site_input_dialog"
                    label="Web site"
                    name="website_url"
                    value={values.website_url}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="school_primary_contact_input_dialog"
                    label="Primary Contact"
                    name="primary_contact"
                    value={values.primary_contact}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.contact && errors.contact}
                    error={Boolean(touched.contact && errors.contact)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="email"
                    variant="outlined"
                    id="school_email_input_dialog"
                    label="Contact Email"
                    name="contact_email"
                    value={values.contact_email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="school_contact_number_input_dialog"
                    label="Contact number"
                    name="contact_number"
                    value={values.contact_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>

              <Divider style={{ margin: "24px 0 16px" }} />

              {/* Section 2: Classification — Plan / Type / Status flags
                  added in TZ-781. org_type == "pro" derives the legacy
                  is_pro_athlete flag at the model layer (api PR #734),
                  so the standalone checkbox is gone. */}
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Classification
              </Typography>
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="school_plan_label">Plan</InputLabel>
                    <Select
                      labelId="school_plan_label"
                      id="school_plan_input_dialog"
                      label="Plan"
                      name="plan"
                      value={values.plan}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <em>—</em>
                      </MenuItem>
                      {ORG_PLAN_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          id={`school_plan_${opt.value}`}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="school_org_type_label">Type</InputLabel>
                    <Select
                      labelId="school_org_type_label"
                      id="school_org_type_input_dialog"
                      label="Type"
                      name="org_type"
                      value={values.org_type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <em>—</em>
                      </MenuItem>
                      {ORG_TYPE_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          id={`school_org_type_${opt.value}`}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="school_status_label">Status</InputLabel>
                    <Select
                      labelId="school_status_label"
                      id="school_status_input_dialog"
                      label="Status"
                      name="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {ORG_STATUS_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          id={`school_status_${opt.value}`}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider style={{ margin: "24px 0 16px" }} />

              {/* Section 3: Features — consolidated org-level toggles. */}
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Features
              </Typography>
              <Grid container style={{ marginTop: 4 }}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="show_chatbot"
                        name="show_chatbot"
                        checked={Boolean(values.show_chatbot)}
                        onChange={handleChange}
                      />
                    }
                    label="Access to Chatbot"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="active_mindflow"
                        name="active_mindflow"
                        checked={Boolean(values.active_mindflow)}
                        onChange={handleChange}
                      />
                    }
                    label="Mindflow"
                  />
                </Grid>
              </Grid>

              <Divider style={{ margin: "24px 0 16px" }} />

              {/* Section 4: Not-Great Dialog Copy — crisis-flow override. */}
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Not-Great Dialog Copy
              </Typography>
              <Grid container style={{ marginTop: 4 }}>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    rows={3}
                    rowsMax={4}
                    fullWidth
                    type="text"
                    placeholder="Not-Great Dialog Copy"
                    id="school_not_great_dialog_copy"
                    variant="outlined"
                    label="Not-Great Dialog Copy"
                    name="not_great_dialog_copy"
                    value={values.not_great_dialog_copy}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                id="cancel_school_btn_dialog"
                onClick={() => setDialog("close")}
                color="secondary"
              >
                Cancel
              </Button>
              <button
                type="submit"
                id="submit_school_btn_dialog"
                disabled={isSubmitting || !values.name || !values.time_zone}
                className="btn btn-primary btn-elevate kt-login__btn-primary d-flex"
              >
                {mode === "editing" ? "Submit" : "Create"}
                {loading && (
                  <CircularProgress
                    color="inherit"
                    size={18}
                    style={{ marginLeft: 10 }}
                  />
                )}
              </button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const mapStateToProps = (store) => ({
  school: store.school.school,
});

const mapDispatchToProps = (dispatch) => ({
  schoolActions: bindActionCreators(schoolDUCK.actions, dispatch),
  settingLoader: bindActionCreators(setLoader, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SchoolDialog);
